import { useEffect, useRef, useState } from 'react'
import type { KeyboardEvent, PointerEvent } from 'react'
import {
  getAvailableRuleOffset,
  getStorybookRuleId,
  type StorybookRule,
} from './storybook-rules'

interface StorybookRulesOverlayProps {
  rules: StorybookRule[]
  onRulesChange: (
    rules: StorybookRule[],
    options?: { replace?: boolean },
  ) => void
}

export function StorybookRulesOverlay({
  rules,
  onRulesChange,
}: StorybookRulesOverlayProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const draggingRuleIndexRef = useRef<number | null>(null)
  const [focusedRuleId, setFocusedRuleId] = useState<string | null>(null)

  useEffect(() => {
    if (!focusedRuleId) return
    rootRef.current
      ?.querySelector<HTMLButtonElement>('[data-focused]')
      ?.focus({ preventScroll: true })
    // oxlint-disable-next-line react/exhaustive-effect-dependencies -- rules changes reorder DOM nodes; refocus selected rule afterward
  }, [focusedRuleId, rules])

  const getMaximumOffset = (rule: StorybookRule) => {
    const bounds = rootRef.current?.getBoundingClientRect()
    if (!bounds) return 0
    return rule.orientation === 'vertical' ? bounds.width : bounds.height
  }

  const moveRule = (index: number, desiredOffset: number) => {
    const rule = rules[index]
    if (!rule) return

    const otherRules = rules.filter((_, ruleIndex) => ruleIndex !== index)
    const offset = getAvailableRuleOffset(
      otherRules,
      rule.orientation,
      desiredOffset,
      getMaximumOffset(rule),
    )
    if (offset === rule.offset) return

    const nextRules = rules.map((currentRule, ruleIndex) =>
      ruleIndex === index ? { ...currentRule, offset } : currentRule,
    )
    setFocusedRuleId(getStorybookRuleId({ ...rule, offset }))
    onRulesChange(nextRules, { replace: true })
  }

  const removeRule = (index: number) => {
    setFocusedRuleId(null)
    onRulesChange(
      rules.filter((_, ruleIndex) => ruleIndex !== index),
      { replace: true },
    )
  }

  const duplicateRule = (index: number) => {
    const rule = rules[index]
    if (!rule) return

    const offset = getAvailableRuleOffset(
      rules,
      rule.orientation,
      rule.offset + 8,
      getMaximumOffset(rule),
    )
    if (
      rules.some(
        (currentRule) =>
          getStorybookRuleId(currentRule) ===
          getStorybookRuleId({ ...rule, offset }),
      )
    ) {
      return
    }

    onRulesChange([...rules, { ...rule, offset }], { replace: true })
  }

  const handleKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    rule: StorybookRule,
    index: number,
  ) => {
    if ((event.metaKey || event.ctrlKey) && event.code === 'KeyD') {
      event.preventDefault()
      duplicateRule(index)
      return
    }
    if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault()
      removeRule(index)
      return
    }

    const delta = event.shiftKey ? 10 : 1
    const movement =
      rule.orientation === 'vertical'
        ? event.key === 'ArrowLeft'
          ? -delta
          : event.key === 'ArrowRight'
            ? delta
            : 0
        : event.key === 'ArrowUp'
          ? -delta
          : event.key === 'ArrowDown'
            ? delta
            : 0
    if (movement === 0) return

    event.preventDefault()
    moveRule(index, rule.offset + movement)
  }

  const handlePointerMove = (
    event: PointerEvent<HTMLDivElement>,
    rule: StorybookRule,
    index: number,
  ) => {
    const bounds = rootRef.current?.getBoundingClientRect()
    if (!bounds) return

    const offset =
      rule.orientation === 'vertical'
        ? event.clientX - bounds.left
        : event.clientY - bounds.top
    moveRule(index, offset)
  }

  return (
    <div
      ref={rootRef}
      className="pointer-events-none fixed inset-0 z-40 overflow-hidden"
      data-slot="storybook-rules"
      onLostPointerCapture={() => {
        draggingRuleIndexRef.current = null
      }}
      onPointerMove={(event) => {
        const index = draggingRuleIndexRef.current
        if (index === null) return
        const rule = rules[index]
        if (rule) handlePointerMove(event, rule, index)
      }}
      onPointerUp={() => {
        draggingRuleIndexRef.current = null
      }}
    >
      {rules.map((rule, index) => {
        const id = getStorybookRuleId(rule)
        const focused = focusedRuleId === id
        const vertical = rule.orientation === 'vertical'

        return (
          <button
            key={id}
            type="button"
            aria-label={`${vertical ? 'Vertical' : 'Horizontal'} rule at ${rule.offset} pixels`}
            className={
              vertical
                ? 'group/rule pointer-events-auto absolute top-0 bottom-0 w-[9px] -translate-x-1/2 cursor-col-resize touch-none border-0 bg-transparent p-0 outline-none'
                : 'group/rule pointer-events-auto absolute right-0 left-0 h-[9px] -translate-y-1/2 cursor-row-resize touch-none border-0 bg-transparent p-0 outline-none'
            }
            data-focused={focused ? '' : undefined}
            data-offset={rule.offset}
            data-orientation={rule.orientation}
            style={vertical ? { left: rule.offset } : { top: rule.offset }}
            onBlur={() => {
              setFocusedRuleId(null)
            }}
            onFocus={() => {
              setFocusedRuleId(id)
            }}
            onKeyDown={(event) => {
              handleKeyDown(event, rule, index)
            }}
            onPointerDown={(event) => {
              event.preventDefault()
              event.currentTarget.focus()
              draggingRuleIndexRef.current = index
              rootRef.current?.setPointerCapture(event.pointerId)
            }}
          >
            <span
              aria-hidden="true"
              className={
                vertical
                  ? 'bg-error absolute top-0 bottom-0 left-1/2 w-px -translate-x-1/2'
                  : 'bg-error absolute top-1/2 right-0 left-0 h-px -translate-y-1/2'
              }
            />
            <span
              className={`${focused ? 'ring-error/40 ring-4' : ''} bg-error text-on-error text-label-small absolute z-10 rounded-sm px-1 py-0.5 leading-none font-medium tabular-nums select-none ${vertical ? 'top-0 left-1/2 -translate-x-1/2' : 'top-1/2 right-0 -translate-y-1/2'}`}
            >
              {rule.offset}
            </span>
          </button>
        )
      })}
    </div>
  )
}
